import { useEffect, useState, useRef, useCallback, Children } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnnotation } from '@embedpdf/plugin-annotation/react'
import { useRedaction } from '@embedpdf/plugin-redaction/react'
import { useHistoryCapability } from '@embedpdf/plugin-history/react'
import { useResponsive } from '../../hooks/useResponsive'
import { usePan } from '@embedpdf/plugin-pan/react'
import { useCapture } from '@embedpdf/plugin-capture/react'
import { useScroll } from '@embedpdf/plugin-scroll/react'
import { useRotate } from '@embedpdf/plugin-rotate/react'
import { useZoom, ZoomMode } from '@embedpdf/plugin-zoom/react'
import { useDocumentManagerCapability } from '@embedpdf/plugin-document-manager/react'
import { useDocumentState } from '@embedpdf/core/react'
import { useExport } from '@embedpdf/plugin-export/react'
import { saveDocument } from '../../services/indexeddb-service'
import { Tooltip } from '../Tooltip'
import { Button } from '../ui/button'
import { Icon } from '../ui/icon'
import { StyleToolPanel } from './StyleToolPanel'
import { ScreenshotOverlay } from './ScreenshotOverlay'
import { AreaOcrOverlay } from './ocr/AreaOcrOverlay'
import { CollapsedMenu } from './CollapsedMenu'
import { OcrService, type PageOcrOptions } from '../../services/ocr/ocr-service'
import { applyToolDefaults } from '../../stores/styleStore'
import { openStampSidebarRef } from './Sidebar'
import type { RightPanelMode } from './SimplePdfViewer'

interface ContextualToolbarProps {
  documentId: string
  activeMode: string
  setIsRightPanelOpen: (open: boolean) => void
  setRightPanelMode: (mode: RightPanelMode) => void
}

const TOOL_MAPPING: Record<string, string> = {
  highlight: 'highlight',
  strikethrough: 'strikeout',
  underline: 'underline',
  squigglyUnderline: 'squiggly',
  ink: 'ink',
  inkHighlighter: 'inkHighlighter',
  freeText: 'freeText',
  textCallout: 'freeTextCallout',
  stickyNote: 'textComment',
  stamp: 'stamp',
  signature: 'stamp',
  image: 'stamp',
  rectangle: 'square',
  oval: 'circle',
  line: 'line',
  arrow: 'lineArrow',
  polygon: 'polygon',
  polyline: 'polyline',
}

export const areaOcrDeactivateRef: { current: (() => void) | null } = { current: null }
export const unsavedChangesRef: { current: boolean } = { current: false }

function ResponsiveToolbarContent({ isCompact, children, persistent, styleTool }: { isCompact: boolean; children: React.ReactNode; persistent?: React.ReactNode; styleTool?: React.ReactNode }) {
  const childArray = Children.toArray(children)
  const inlineChildren = childArray.slice(0, 4)
  const collapsedChildren = childArray.slice(4)
  
  if (isCompact) {
    return (
      <div className="h-12 bg-bg-surface flex items-center justify-between px-2 border-b-[0.5px] border-border-default/50 shadow-sm relative z-10">
        <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
          {styleTool}
          {inlineChildren}
          {persistent}
        </div>
        {collapsedChildren.length > 0 && (
          <CollapsedMenu>
            <div className="flex items-center gap-2 flex-wrap max-w-[280px]">
              {collapsedChildren}
            </div>
          </CollapsedMenu>
        )}
      </div>
    )
  }
  return (
    <div className="h-12 bg-bg-surface flex items-center justify-center px-4 border-b-[0.5px] border-border-default/50 shadow-sm relative z-10">
      <div className="flex items-center justify-center gap-4">
        {children}
        {persistent}
      </div>
    </div>
  )
}

export const ContextualToolbar = ({ documentId, activeMode, setIsRightPanelOpen, setRightPanelMode }: ContextualToolbarProps) => {
  const { t } = useTranslation()
  const { provides: annotationApi, state } = useAnnotation(documentId)
  const { isPanning, provides: panApi } = usePan(documentId)
  const { provides: captureApi, state: captureState } = useCapture(documentId)
  const { state: scrollState } = useScroll(documentId)
  const { provides: rotateApi } = useRotate(documentId)
  const { provides: zoomApi, state: zoomState } = useZoom(documentId)
  const { provides: historyApi } = useHistoryCapability()
  const { state: redactionState, provides: redactionApi } = useRedaction(documentId)
  const docState = useDocumentState(documentId)
  const { provides: exportApi } = useExport(documentId)
  const isCompact = useResponsive()
  interface PendingCapture {
    blob: Blob
    filename: string
    objectUrl: string
    pageIndex: number
    rect: { origin: { x: number; y: number }; size: { width: number; height: number } }
  }

const { provides: docManager } = useDocumentManagerCapability()
const [screenshotCounters, setScreenshotCounters] = useState<Record<number, number>>({})
const [isScreenshotActive, setIsScreenshotActive] = useState(false)
const [pendingCapture, setPendingCapture] = useState<PendingCapture | null>(null)
const countersRef = useRef<Record<number, number>>({})
const screenshotRef = useRef(false)
const [isOcrProcessing, setIsOcrProcessing] = useState(false)
const [ocrProgress, setOcrProgress] = useState(OcrService.progress)
const [isAreaOcrActive, setIsAreaOcrActive] = useState(false)
  const [isPageOcrActive, setIsPageOcrActive] = useState(false)
  const areaOcrRef = useRef(false)
  const [stampSubTool, setStampSubTool] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDictionaryActive, setIsDictionaryActive] = useState(false)
interface AreaOcrCapture {
  pageIndex: number
  rect: { origin: { x: number; y: number }; size: { width: number; height: number } }
}
const [areaOcrCapture, setAreaOcrCapture] = useState<AreaOcrCapture | null>(null)

const currentPage = (scrollState?.currentPage ?? 1) - 1
const totalPages = docState?.document?.pages?.length ?? 1

useEffect(() => {
  return OcrService.subscribe(() => {
    setIsOcrProcessing(OcrService.isProcessing)
    setOcrProgress(OcrService.progress)
  })
}, [])

useEffect(() => {
  OcrService.setPageOcrCallback(handlePageOcrConfirm)
  return () => {
    OcrService.setPageOcrCallback(null)
  }
})

useEffect(() => {
  countersRef.current = screenshotCounters
}, [screenshotCounters])

const activeToolId = state?.activeToolId ?? null
const isMarqueeActive = captureState?.isMarqueeCaptureActive ?? false

useEffect(() => {
  areaOcrDeactivateRef.current = () => {
    setIsAreaOcrActive(false)
    areaOcrRef.current = false
  }
  return () => {
    areaOcrDeactivateRef.current = null
  }
}, [])

useEffect(() => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isAreaOcrActive && !areaOcrCapture) {
      setIsAreaOcrActive(false)
      areaOcrRef.current = false
      captureApi?.disableMarqueeCapture()
    }
  }
  const handleContextMenu = () => {
    if (isAreaOcrActive && !areaOcrCapture) {
      setIsAreaOcrActive(false)
      areaOcrRef.current = false
      captureApi?.disableMarqueeCapture()
    }
  }
  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('contextmenu', handleContextMenu)
  return () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('contextmenu', handleContextMenu)
  }
}, [isAreaOcrActive, areaOcrCapture, captureApi])

const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
const savePendingRef = useRef(false)
const doSaveRef = useRef<(() => void) | null>(null)

useEffect(() => {
  if (!annotationApi || !exportApi || !docManager) return
  const docMeta = docManager.getDocumentState(documentId)
  const docName = docMeta?.name || docMeta?.id || 'annotated-document.pdf'

  const doSave = async () => {
    if (savePendingRef.current) return
    savePendingRef.current = true
    try {
      const task = exportApi.saveAsCopy()
      if (!task) return
      task.wait(async (buffer: ArrayBuffer) => {
        try {
          const docRecord = {
            id: docName,
            name: docName,
            buffer,
            byteLength: buffer.byteLength,
            currentPage: 0,
            lastOpened: Date.now(),
          }
          await saveDocument(docRecord)
          await saveDocument({ ...docRecord, id: 'current-document' })
          unsavedChangesRef.current = false
        } catch {}
        savePendingRef.current = false
      }, () => { savePendingRef.current = false })
    } catch {
      savePendingRef.current = false
    }
  }

  doSaveRef.current = doSave

  const debouncedSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(doSave, 1500)
  }

  const unsub = annotationApi.onAnnotationEvent((event: any) => {
    if (event.type === 'create' || event.type === 'update' || event.type === 'delete') {
      unsavedChangesRef.current = true
      debouncedSave()
    }
  })

  return () => {
    unsub?.()
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
  }
}, [annotationApi, exportApi, docManager, documentId])

useEffect(() => {
  const handler = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    doSaveRef.current?.()
  }
  window.addEventListener('opakor-force-save', handler)
  return () => window.removeEventListener('opakor-force-save', handler)
}, [])

const sanitizeFilename = useCallback((name: string, maxLen: number = 40): string => {
  return name
    .replace(/[^a-zA-Z0-9\-_\u1780-\u17FF\u0E00-\u0E7F]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, maxLen)
    .replace(/_$/g, '')
}, [])

const handleSaveScreenshot = useCallback(() => {
  if (!pendingCapture) return
  const a = document.createElement('a')
  a.href = pendingCapture.objectUrl
  a.download = pendingCapture.filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(pendingCapture.objectUrl)
  setPendingCapture(null)
}, [pendingCapture])

const handleCancelScreenshot = useCallback(() => {
  if (!pendingCapture) return
  URL.revokeObjectURL(pendingCapture.objectUrl)
  setPendingCapture(null)
}, [pendingCapture])

const deactivateAllTools = useCallback(() => {
  if (isAreaOcrActive) {
    setIsAreaOcrActive(false)
    areaOcrRef.current = false
    captureApi?.disableMarqueeCapture()
  }
  if (isScreenshotActive) {
    screenshotRef.current = false
    setIsScreenshotActive(false)
    captureApi?.disableMarqueeCapture()
  }
  if (pendingCapture) {
    handleCancelScreenshot()
  }
  if (redactionState?.activeType === 'marqueeRedact') {
    redactionApi?.toggleMarqueeRedact()
  }
  if (redactionState?.activeType === 'redactSelection') {
    redactionApi?.toggleRedactSelection()
  }
  if (activeToolId && annotationApi) {
    annotationApi.setActiveTool(null)
  }
  if (isPanning) {
    panApi?.disablePan()
  }
  if (isMarqueeActive && !isAreaOcrActive && !isScreenshotActive) {
    captureApi?.disableMarqueeCapture()
  }
  setIsPageOcrActive(false)
}, [isAreaOcrActive, isScreenshotActive, pendingCapture, handleCancelScreenshot, redactionState, redactionApi, activeToolId, annotationApi, isPanning, panApi, isMarqueeActive, captureApi])

useEffect(() => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      deactivateAllTools()
    }
  }
  const handleContextMenu = () => {
    deactivateAllTools()
  }
  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('contextmenu', handleContextMenu)
  return () => {
    document.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('contextmenu', handleContextMenu)
  }
}, [deactivateAllTools])

const handleScreenshot = useCallback(() => {
  if (!captureApi) return

  if (screenshotRef.current) {
    captureApi.toggleMarqueeCapture()
    screenshotRef.current = false
    setIsScreenshotActive(false)
    return
  }

  if (pendingCapture) {
    handleCancelScreenshot()
    return
  }

  if (isAreaOcrActive) {
    setIsAreaOcrActive(false)
    areaOcrRef.current = false
    captureApi?.disableMarqueeCapture()
  }

  if (activeToolId && annotationApi) {
    annotationApi.setActiveTool(null)
  }

  setIsPageOcrActive(false)

  screenshotRef.current = true
  setIsScreenshotActive(true)
  captureApi.toggleMarqueeCapture()
}, [captureApi, pendingCapture, handleCancelScreenshot, isAreaOcrActive, activeToolId, annotationApi])

  useEffect(() => {
    if (!captureApi) return

    const unsub = captureApi.onCaptureArea((event) => {
      if (areaOcrRef.current) {
        areaOcrRef.current = false
        setAreaOcrCapture({
          pageIndex: event.pageIndex,
          rect: event.rect,
        })
        return
      }

      if (!screenshotRef.current) return

      screenshotRef.current = false
      setIsScreenshotActive(false)

      const docState = docManager?.getDocumentState(documentId)
      let docName = docState?.id ?? 'document'
      if (docState?.name) {
        docName = docState.name
      } else if (docState?.id) {
        docName = docState.id.replace(/^[a-f0-9-]+$/, 'document')
      }

      const safeName = sanitizeFilename(docName)
      const paddedPage = String(event.pageIndex + 1).padStart(3, '0')

      const prevCounters = countersRef.current
      const pageCounter = (prevCounters[event.pageIndex] || 0) + 1
      const newCounters = { ...prevCounters, [event.pageIndex]: pageCounter }
      countersRef.current = newCounters
      setScreenshotCounters(newCounters)

      const maxNameLen = 120 - 17
      const truncatedName = safeName.slice(0, maxNameLen)
      const filename = `${truncatedName}_page_${paddedPage}_${pageCounter}.png`

      const objectUrl = URL.createObjectURL(event.blob)
      setPendingCapture({
        blob: event.blob,
        filename,
        objectUrl,
        pageIndex: event.pageIndex,
        rect: event.rect,
      })
    })

    return () => { unsub() }
  }, [captureApi, docManager, sanitizeFilename, documentId])

  const handleSetTool = (toolId: string) => {
    const annotationToolId = TOOL_MAPPING[toolId]
    if (!annotationToolId) {
      console.warn('Unknown tool ID:', toolId)
      return
    }

    if (isAreaOcrActive) {
      captureApi?.disableMarqueeCapture()
      setIsAreaOcrActive(false)
      areaOcrRef.current = false
    }

    if (isScreenshotActive) {
      screenshotRef.current = false
      setIsScreenshotActive(false)
      captureApi?.disableMarqueeCapture()
    }

    setIsPageOcrActive(false)

    const isStampTool = annotationToolId === 'stamp'
    if (!isStampTool) setStampSubTool(null)

    const newTool = activeToolId === annotationToolId ? null : annotationToolId
    annotationApi?.setActiveTool?.(newTool)
    applyToolDefaults(newTool)
    if (!newTool) setStampSubTool(null)
  }

  const handleOcr = useCallback(() => {
    if (isAreaOcrActive) {
      captureApi?.disableMarqueeCapture()
      setIsAreaOcrActive(false)
      areaOcrRef.current = false
    }
    if (isScreenshotActive) {
      screenshotRef.current = false
      setIsScreenshotActive(false)
      captureApi?.disableMarqueeCapture()
    }
    if (activeToolId && annotationApi) {
      annotationApi.setActiveTool(null)
    }
    setIsPageOcrActive(true)
    setIsRightPanelOpen(true)
    setIsDictionaryActive(false)
    setRightPanelMode('ocrResults')
    OcrService.openPageOcrForm(currentPage, totalPages)
  }, [captureApi, isAreaOcrActive, isScreenshotActive, activeToolId, annotationApi, setIsRightPanelOpen, setRightPanelMode, currentPage, totalPages])

  const handlePageOcrConfirm = useCallback(async (options: PageOcrOptions) => {
    const engine = OcrService.pdfEngine
    const doc = docState?.document
    if (!engine || !doc) return

    try {
      await OcrService.initialize()

      const startPage = options.scope === 'all' ? 0 : options.fromPage
      const endPage = options.scope === 'all' ? totalPages - 1 : options.toPage

      for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
        const page = doc.pages[pageIndex]
        if (!page) continue

        OcrService.setPageProgress(pageIndex, endPage)

        const imageResult = await engine.renderPageRaw(doc, page).toPromise()
        if (!imageResult) continue

        await OcrService.runOcr({
          data: imageResult.data,
          width: imageResult.width,
          height: imageResult.height,
        }, pageIndex, `${OcrService.documentName || 'document'}_page_${pageIndex}`)
      }
    } catch (err) {
      console.error('OCR failed:', err)
    }
  }, [docState?.document, totalPages])

  const handleAreaOcr = useCallback(() => {
    if (!captureApi) return

    if (isAreaOcrActive) {
      captureApi.disableMarqueeCapture()
      setIsAreaOcrActive(false)
      areaOcrRef.current = false
      return
    }

    if (isScreenshotActive) {
      screenshotRef.current = false
      setIsScreenshotActive(false)
      captureApi.disableMarqueeCapture()
    }
    if (pendingCapture) {
      handleCancelScreenshot()
    }

    setIsPageOcrActive(false)

    if (activeToolId && annotationApi) {
      annotationApi.setActiveTool(null)
    }

    setIsRightPanelOpen(true)
    setIsDictionaryActive(false)
    setRightPanelMode('ocrResults')
    areaOcrRef.current = true
    setIsAreaOcrActive(true)
    captureApi.toggleMarqueeCapture()
  }, [captureApi, isAreaOcrActive, isScreenshotActive, pendingCapture, handleCancelScreenshot, activeToolId, annotationApi, setIsRightPanelOpen, setRightPanelMode])

  const handleAreaOcrConfirm = useCallback(async () => {
    if (!areaOcrCapture) return
    const engine = OcrService.pdfEngine
    const doc = docState?.document
    if (!engine || !doc) return

    OcrService.startProcessing(true)

    try {
      await OcrService.initialize()

      const page = doc.pages[areaOcrCapture.pageIndex]
      if (!page) return

      const imageResult = await engine.renderPageRectRaw(
        doc,
        page,
        {
          origin: areaOcrCapture.rect.origin,
          size: areaOcrCapture.rect.size,
        },
      ).toPromise()
      if (!imageResult) return

      setAreaOcrCapture(null)

      const cacheKey = `${OcrService.documentName || 'document'}_area_${areaOcrCapture.pageIndex}_${Math.round(areaOcrCapture.rect.origin.x)}_${Math.round(areaOcrCapture.rect.origin.y)}_${Math.round(areaOcrCapture.rect.size.width)}_${Math.round(areaOcrCapture.rect.size.height)}`
      await OcrService.runOcr({
        data: imageResult.data,
        width: imageResult.width,
        height: imageResult.height,
      }, areaOcrCapture.pageIndex, cacheKey)
    } catch (err) {
      console.error('Area OCR failed:', err)
    } finally {
      areaOcrRef.current = true
      captureApi?.toggleMarqueeCapture()
    }
  }, [areaOcrCapture, docState?.document, captureApi])

  const handleAreaOcrCancel = useCallback(() => {
    setAreaOcrCapture(null)
    areaOcrRef.current = true
    captureApi?.toggleMarqueeCapture()
  }, [captureApi])

  const handleShare = useCallback(async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ url })
      } catch {}
      return
    }
    try {
      await navigator.clipboard.writeText(url)
    } catch {}
}, [])

useEffect(() => {
  const handler = () => setIsFullscreen(!!document.fullscreenElement)
  document.addEventListener('fullscreenchange', handler)
  return () => document.removeEventListener('fullscreenchange', handler)
}, [])

const handleFullscreen = useCallback(() => {
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    document.documentElement.requestFullscreen()
  }
}, [])

  const handleExport = useCallback(() => {
    exportApi?.download()
  }, [exportApi])

  // Common history buttons
  const HistoryButtons = () => (
    <div className="flex items-center gap-1">
      <Tooltip content={t('tooltips.undo') || 'Undo'} position="bottom">
        <Button 
          onClick={() => historyApi?.undo()}
          disabled={!historyApi?.canUndo()}
          className="text-fg-muted hover:text-fg-secondary"
        >
          <Icon icon="arrowBackUp" size={18} />
        </Button>
      </Tooltip>
      <Tooltip content={t('tooltips.redo') || 'Redo'} position="bottom">
        <Button 
          onClick={() => historyApi?.redo()}
          disabled={!historyApi?.canRedo()}
          className="text-fg-muted hover:text-fg-secondary"
        >
          <Icon icon="arrowForwardUp" size={18} />
        </Button>
      </Tooltip>
    </div>
  )

  const ToolDivider = () => <div className="h-4 w-px bg-border-default mx-1" />

  // Annotate Tab Tools
  let toolbar: JSX.Element | null = null

  if (activeMode === 'Annotate') {
    toolbar = (
      <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>
        <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
          <Tooltip content={t('tooltips.highlight') || 'Highlight'} position="bottom">
            <Button
              onClick={() => handleSetTool('highlight')}
              active={activeToolId === 'highlight'}
              className={activeToolId === 'highlight' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="highlight" size={18} primaryColor="#FACC15" />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.strikethrough') || 'Strikethrough'} position="bottom">
            <Button
              onClick={() => handleSetTool('strikethrough')}
              active={activeToolId === 'strikeout'}
              className={activeToolId === 'strikeout' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="strikethrough" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.underline') || 'Underline'} position="bottom">
            <Button
              onClick={() => handleSetTool('underline')}
              active={activeToolId === 'underline'}
              className={activeToolId === 'underline' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="underline" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.squigglyUnderline') || 'Squiggly Underline'} position="bottom">
            <Button
              onClick={() => handleSetTool('squigglyUnderline')}
              active={activeToolId === 'squiggly'}
              className={activeToolId === 'squiggly' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="squiggly" size={18} />
            </Button>
          </Tooltip>
        </div>

        <div className="h-6 w-px bg-border-default" />

        <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
          <Tooltip content={t('tooltips.pencil') || 'Pencil'} position="bottom">
            <Button
              onClick={() => handleSetTool('ink')}
              active={activeToolId === 'ink'}
              className={activeToolId === 'ink' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="pencilMarker" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.highlighter') || 'Highlighter'} position="bottom">
            <Button
              onClick={() => handleSetTool('inkHighlighter')}
              active={activeToolId === 'inkHighlighter'}
              className={activeToolId === 'inkHighlighter' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="inkHighlighter" size={18} />
            </Button>
          </Tooltip>
        </div>

        <div className="h-6 w-px bg-border-default" />

        <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
          <Tooltip content={t('tooltips.textBox') || 'Text Box'} position="bottom">
            <Button
              onClick={() => handleSetTool('freeText')}
              active={activeToolId === 'freeText'}
              className={activeToolId === 'freeText' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="freeText" size={18} />
            </Button>
          </Tooltip>

        </div>

        <div className="h-6 w-px bg-border-default" />

        <div className="flex items-center gap-2 rounded-lg bg-bg-input/80 px-2 py-1">
          <StyleToolPanel activeMode={activeMode} activeToolId={activeToolId} />
        </div>

      </ResponsiveToolbarContent>
    )
  }

  // Insert Tab Tools
  if (activeMode === 'Insert') {
    toolbar = (
      <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>
        <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
          <Tooltip content={t('tooltips.signature') || 'Signature'} position="bottom">
            <Button 
              onClick={() => { handleSetTool('signature'); setStampSubTool('signature') }}
              active={activeToolId === 'stamp' && stampSubTool === 'signature'}
              className={activeToolId === 'stamp' && stampSubTool === 'signature' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="signature" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.image') || 'Image'} position="bottom">
            <Button 
              onClick={() => { handleSetTool('image'); setStampSubTool('image') }}
              active={activeToolId === 'stamp' && stampSubTool === 'image'}
              className={activeToolId === 'stamp' && stampSubTool === 'image' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="photo" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.stamp') || 'Stamp'} position="bottom">
            <Button 
              onClick={() => { handleSetTool('stamp'); setStampSubTool('stamp'); openStampSidebarRef.current?.() }}
              active={activeToolId === 'stamp' && stampSubTool === 'stamp'}
              className={activeToolId === 'stamp' && stampSubTool === 'stamp' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="rubberStamp" size={18} />
            </Button>
          </Tooltip>
        </div>

        <ToolDivider />

        <div className="flex items-center gap-3">
          <StyleToolPanel activeMode={activeMode} activeToolId={activeToolId} />
        </div>
      </ResponsiveToolbarContent>
    )
  }

  // Shapes Tab Tools
  if (activeMode === 'Shapes') {
    toolbar = (
      <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>
        <div className="flex items-center gap-1">
          <Tooltip content={t('tooltips.rectangle') || 'Rectangle'} position="bottom">
            <Button 
              onClick={() => handleSetTool('rectangle')}
              active={activeToolId === 'rectangle'}
              className={activeToolId === 'rectangle' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="square" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.oval') || 'Oval'} position="bottom">
            <Button 
              onClick={() => handleSetTool('oval')}
              active={activeToolId === 'oval'}
              className={activeToolId === 'oval' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="circle" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.line') || 'Line'} position="bottom">
            <Button 
              onClick={() => handleSetTool('line')}
              active={activeToolId === 'line'}
              className={activeToolId === 'line' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="line" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.arrow') || 'Arrow'} position="bottom">
            <Button 
              onClick={() => handleSetTool('arrow')}
              active={activeToolId === 'arrow'}
              className={activeToolId === 'arrow' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="lineArrow" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.polygon') || 'Polygon'} position="bottom">
            <Button 
              onClick={() => handleSetTool('polygon')}
              active={activeToolId === 'polygon'}
              className={activeToolId === 'polygon' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="polygon" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.polyline') || 'Polyline'} position="bottom">
            <Button 
              onClick={() => handleSetTool('polyline')}
              active={activeToolId === 'polyline'}
              className={activeToolId === 'polyline' ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="zigzag" size={18} />
            </Button>
          </Tooltip>
        </div>

        <ToolDivider />

        <div className="flex items-center gap-3">
          <StyleToolPanel activeMode={activeMode} activeToolId={activeToolId} />
        </div>
      </ResponsiveToolbarContent>
    )
  }

  // Redact Tab Tools
  if (activeMode === 'Redact') {
    const isRedactingArea = redactionState?.activeType === 'marqueeRedact'
    const isRedactingText = redactionState?.activeType === 'redactSelection'

    toolbar = (
      <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>

        {/* Redact Area and Text Tools */}
        <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
          <Tooltip content={t('tooltips.redactArea') || 'Redact Area'} position="bottom">
            <Button 
              onClick={() => redactionApi?.toggleMarqueeRedact()}
              active={isRedactingArea}
              className={isRedactingArea ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="redactArea" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.redactText') || 'Redact Text'} position="bottom">
            <Button 
              onClick={() => redactionApi?.toggleRedactSelection()}
              active={isRedactingText}
              className={isRedactingText ? '' : 'text-fg-secondary hover:text-fg-primary'}
            >
              <Icon icon="redact" size={18} />
            </Button>
          </Tooltip>
        </div>

        <ToolDivider />

        <div className="flex items-center gap-1">
          <Tooltip content={t('tooltips.applyRedactions') || 'Apply All Redactions'} position="bottom">
            <Button
              onClick={() => redactionApi?.commitAllPending()}
              className="text-fg-secondary hover:text-fg-primary"
            >
              <Icon icon="check" size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t('tooltips.clearRedactions') || 'Clear All Redactions'} position="bottom">
            <Button
              onClick={() => redactionApi?.clearPending()}
              className="text-fg-secondary hover:text-fg-primary"
            >
              <Icon icon="trash" size={18} />
            </Button>
          </Tooltip>
        </div>
      </ResponsiveToolbarContent>
     )
  }

    // Research Tab Tools
    if (activeMode === 'Research') {
     toolbar = (
      <>
        {pendingCapture && (
          <ScreenshotOverlay
            pageIndex={pendingCapture.pageIndex}
            rect={pendingCapture.rect}
            zoomLevel={zoomState?.currentZoomLevel ?? 1}
            onSave={handleSaveScreenshot}
            onCancel={handleCancelScreenshot}
          />
        )}
        {areaOcrCapture && (
          <AreaOcrOverlay
            pageIndex={areaOcrCapture.pageIndex}
            rect={areaOcrCapture.rect}
            zoomLevel={zoomState?.currentZoomLevel ?? 1}
            progress={ocrProgress}
            onOcr={handleAreaOcrConfirm}
            onCancel={handleAreaOcrCancel}
          />
        )}
        <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>
          {/* Research Tools */}
          <div className="flex items-center gap-1">
            <Tooltip content={t('tooltips.ocr') || 'OCR (Text Recognition)'} position="bottom">
              <Button
                onClick={handleOcr}
                active={isPageOcrActive}
                disabled={isOcrProcessing}
                className={isPageOcrActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="pageOcr" size={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.areaOcr') || 'Area OCR'} position="bottom">
              <Button
                onClick={handleAreaOcr}
                active={isAreaOcrActive}
                disabled={isOcrProcessing}
                className={isAreaOcrActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="areaOcr" size={18} />
              </Button>
            </Tooltip>
            <Tooltip content={isScreenshotActive ? (t('tooltips.screenshotCancel') || 'Click again to cancel') : (t('tooltips.screenshot') || 'Screenshot')} position="bottom">
              <Button 
                onClick={handleScreenshot}
                active={isScreenshotActive}
                className={isScreenshotActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="screenshot" size={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.search') || 'Search'} position="bottom">
              <Button 
                className="text-fg-secondary hover:text-fg-primary"
              >
                <Icon icon="search" size={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('dashboard.dictionary') || 'Dictionary'} position="bottom">
              <Button
                onClick={() => {
                  setRightPanelMode('dictionary');
                  setIsRightPanelOpen(true);
                  setIsDictionaryActive(true);
                  window.dispatchEvent(new CustomEvent('opakor-open-dictionary'));
                }}
                active={isDictionaryActive}
                className={isDictionaryActive ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="book" size={18} />
              </Button>
            </Tooltip>
          </div>

          <ToolDivider />

          <div className="flex items-center gap-1">
            <Tooltip content={t('tooltips.highlight') || 'Highlight'} position="bottom">
              <Button 
                onClick={() => handleSetTool('highlight')}
                active={activeToolId === 'highlight'}
                className={activeToolId === 'highlight' ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="highlight" size={18} primaryColor="#FACC15" />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.stickyNote') || 'Sticky Note'} position="bottom">
              <Button 
                onClick={() => {
                  if (activeToolId !== 'textComment') {
                    setIsDictionaryActive(false)
                    setRightPanelMode('comments')
                    setIsRightPanelOpen(true)
                  }
                  handleSetTool('stickyNote')
                }}
                active={activeToolId === 'textComment'}
                className={activeToolId === 'textComment' ? '' : 'text-fg-secondary hover:text-fg-primary'}
              >
                <Icon icon="comment" size={18} primaryColor="#FACC15" />
              </Button>
            </Tooltip>
          </div>

          <ToolDivider />

          <div className="flex items-center gap-1">
            <Tooltip content={t('tooltips.share') || 'Share'} position="bottom">
              <Button 
                onClick={handleShare}
                className="text-fg-secondary hover:text-fg-primary"
              >
                <Icon icon="externalLink" size={18} />
              </Button>
            </Tooltip>
            <Tooltip content={t('tooltips.export') || 'Export'} position="bottom">
              <Button
                onClick={handleExport}
                className="text-fg-secondary hover:text-fg-primary"
              >
                <Icon icon="download" size={18} />
              </Button>
            </Tooltip>
          </div>

          <ToolDivider />

          <div className="flex items-center gap-3">
            <StyleToolPanel activeMode={activeMode} activeToolId={activeToolId} />
          </div>
        </ResponsiveToolbarContent>
      </>
    )
   }

   // View mode - fit and rotate tools
   if (activeMode === 'View') {
     toolbar = (
       <ResponsiveToolbarContent isCompact={isCompact} persistent={<HistoryButtons />}>

         {/* Fit Mode Tools */}
         <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
           <Tooltip content={t('tooltips.fitToPage') || 'Fit to Page'} position="bottom">
             <Button
               onClick={() => zoomApi?.requestZoom(ZoomMode.FitPage)}
               className="text-fg-secondary hover:text-fg-primary"
             >
               <Icon icon="fitToPage" size={18} />
             </Button>
           </Tooltip>
           <Tooltip content={t('tooltips.fitToWidth') || 'Fit to Width'} position="bottom">
             <Button
               onClick={() => zoomApi?.requestZoom(ZoomMode.FitWidth)}
               className="text-fg-secondary hover:text-fg-primary"
             >
               <Icon icon="fitToWidth" size={18} />
             </Button>
           </Tooltip>
         </div>

         <ToolDivider />

         {/* Rotate Tools */}
         <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
           <Tooltip content={t('tooltips.rotateCounterClockwise') || 'Rotate Counter-Clockwise'} position="bottom">
             <Button
               onClick={() => rotateApi?.rotateBackward()}
               className="text-fg-secondary hover:text-fg-primary"
             >
               <Icon icon="rotateCounterClockwise" size={18} />
             </Button>
           </Tooltip>
           <Tooltip content={t('tooltips.rotateClockwise') || 'Rotate Clockwise'} position="bottom">
             <Button
               onClick={() => rotateApi?.rotateForward()}
               className="text-fg-secondary hover:text-fg-primary"
             >
               <Icon icon="rotateClockwise" size={18} />
             </Button>
           </Tooltip>
          </div>

          <ToolDivider />

          <div className="flex items-center gap-1 rounded-lg bg-bg-input/80 px-2 py-1">
            <Tooltip content={isFullscreen ? (t('tooltips.fullscreenExit') || 'Exit Fullscreen') : (t('tooltips.fullscreen') || 'Fullscreen')} position="bottom">
              <Button
                onClick={handleFullscreen}
                className="text-fg-secondary hover:text-fg-primary"
              >
                <Icon icon={isFullscreen ? 'fullscreenExit' : 'fullscreen'} size={18} />
              </Button>
            </Tooltip>
          </div>

          <ToolDivider />
        </ResponsiveToolbarContent>
     )
   }

   // View mode - no contextual toolbar needed
   return (
     <>
        {toolbar}
      </>
    )
 }
